import type { JSX } from "react";
import { memo, useCallback } from "react";

import type { IFieldProps } from "../field";

import { DatePicker } from "@components/formControls/datePicker";
import { Field } from "@/components/fastFields/field/field";
import { convertToAPIDate, convertToDate } from "@/utils";
import { useReadOnly } from "@/hooks";

type IProps<T> = {
  disabled?: boolean;
  placeholder?: string;
  readonly?: boolean;
  onlyYear?: boolean;
  minDate?: Date;
  /**
   * set it true if you want to date in Y-M-D format that backend API
   *
   * By default is `true`
   */
  convertToAPIFormat?: boolean;
} & Required<Pick<IFieldProps<T>, "formik" | "name">> &
  Partial<Pick<IFieldProps<T>, "icon" | "title" | "isRequired">>;

export const FastDate = memo(function FastDate<T>(props: IProps<T>) {
  const { convertToAPIFormat = true } = props;
  const readonly = useReadOnly(props.readonly);
  const icon = props.icon || "Calendar";
  const name = props.name;
  const { values, setFieldValue, errors, touched, handleBlur } = props.formik;
  const value = values[name] as Date;
  const placeholder = props.placeholder;

  const handleChange = useCallback(
    (val: string) => {
      setFieldValue(
        name.toString(),
        convertToAPIFormat ? convertToAPIDate(val, true) : val,
      );
    },
    [name, convertToAPIFormat, setFieldValue],
  );

  return (
    <Field
      name={props.name}
      title={props.title}
      formik={props.formik}
      icon={icon}
      isRequired={props.isRequired && !readonly}
      datepicker
    >
      <DatePicker
        onlyYear={props.onlyYear}
        disabled={props.disabled}
        minDate={props.minDate}
        placeholder={placeholder}
        id={name.toString()}
        onChangeValue={handleChange}
        onBlur={handleBlur}
        date={convertToDate(value)}
        validation={touched[name] && errors[name] ? "error" : undefined}
        readOnly={readonly}
      />
    </Field>
  );
}) as unknown as <T>(props: IProps<T>) => JSX.Element;
